import { RISK_IDENTITY_VERSION } from './identity-shared';

let cachedRiskContext = null;

const normalizeToken = (value, fallback = 'unknown') => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return fallback;
  return normalized
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9/_+.-]/g, '')
    .slice(0, 48) || fallback;
};

const detectPlatformFamily = (userAgent) => {
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios';
  if (/android/i.test(userAgent)) return 'android';
  if (/cros/i.test(userAgent)) return 'chromeos';
  if (/windows/i.test(userAgent)) return 'windows';
  if (/macintosh|mac os x/i.test(userAgent)) return 'macos';
  if (/linux/i.test(userAgent)) return 'linux';
  return 'other';
};

const detectBrowser = (userAgent) => {
  const patterns = [
    { family: 'edge', regex: /edg\/(\d+)/i },
    { family: 'opera', regex: /opr\/(\d+)/i },
    { family: 'samsung', regex: /samsungbrowser\/(\d+)/i },
    { family: 'firefox', regex: /firefox\/(\d+)/i },
    { family: 'chrome', regex: /(?:chrome|crios)\/(\d+)/i },
    { family: 'safari', regex: /version\/(\d+).+safari/i },
  ];

  for (const pattern of patterns) {
    const match = userAgent.match(pattern.regex);
    if (match) {
      return {
        browserFamily: pattern.family,
        browserMajor: normalizeToken(match[1], '0'),
      };
    }
  }

  return {
    browserFamily: 'other',
    browserMajor: '0',
  };
};

const getLanguagePrimary = () => {
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return normalizeToken(String(navigator.languages[0] || '').split(',')[0], 'unknown');
  }
  return normalizeToken(String(navigator.language || '').split(',')[0], 'unknown');
};

const getTimezone = () => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return normalizeToken(timeZone, 'unknown');
  } catch {
    return 'unknown';
  }
};

const getScreenBucket = () => {
  const screenWidth = Number(window.screen?.width || window.innerWidth || 0);
  const screenHeight = Number(window.screen?.height || window.innerHeight || 0);
  const shortEdge = Math.min(screenWidth, screenHeight);
  const longEdge = Math.max(screenWidth, screenHeight);

  if (longEdge <= 740) return 'phone_small';
  if (shortEdge <= 430 && longEdge <= 980) return 'phone_large';
  if (shortEdge <= 900 && longEdge <= 1400) return 'tablet';
  if (longEdge >= 2200) return 'desktop_1440p_plus';
  if (longEdge >= 1600) return 'desktop_1080p_plus';
  return 'desktop_standard';
};

const getDprBucket = () => {
  const dpr = Number(window.devicePixelRatio || 1);
  if (dpr >= 3) return '3_plus';
  if (dpr >= 2) return '2';
  return '1';
};

const getTouchBucket = () => {
  const points = Number(navigator.maxTouchPoints || 0);
  if (points >= 5) return '5_plus';
  if (points >= 2) return '2_4';
  if (points >= 1) return '1';
  return '0';
};

const getHardwareConcurrencyBucket = () => {
  const cores = Number(navigator.hardwareConcurrency || 0);
  if (cores >= 9) return '9_plus';
  if (cores >= 5) return '5_8';
  if (cores >= 3) return '3_4';
  if (cores >= 1) return '1_2';
  return 'unknown';
};

const getDeviceMemoryBucket = () => {
  const memory = Number(navigator.deviceMemory || 0);
  if (!memory) return 'unknown';
  if (memory >= 16) return '16_plus';
  if (memory >= 8) return '8';
  if (memory >= 4) return '4';
  return 'lte_2';
};

export function buildClientRiskContext() {
  if (cachedRiskContext) {
    return cachedRiskContext;
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = String(navigator.userAgent || '');
  const { browserFamily, browserMajor } = detectBrowser(userAgent);
  const platformFamily = detectPlatformFamily(userAgent);
  const mobile = /mobile|iphone|ipod|android.+mobile/i.test(userAgent)
    || navigator.userAgentData?.mobile === true;

  cachedRiskContext = {
    v: RISK_IDENTITY_VERSION,
    platformFamily,
    browserFamily,
    browserMajor,
    mobile,
    languagePrimary: getLanguagePrimary(),
    timezone: getTimezone(),
    screenBucket: getScreenBucket(),
    dprBucket: getDprBucket(),
    touchBucket: getTouchBucket(),
    hardwareConcurrencyBucket: getHardwareConcurrencyBucket(),
    deviceMemoryBucket: getDeviceMemoryBucket(),
  };

  return cachedRiskContext;
}
